import { Component, inject, input, OnDestroy, OnInit } from '@angular/core';
import { InspectionFormWrapper } from '@pages/inspection-detail-page-v2/inspection-form';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  map,
  Observable,
  of,
  startWith,
  Subject,
  switchMap,
  takeUntil,
} from 'rxjs';
import {
  getCheckpointString,
  InspectionEntryFormType,
  InspectionFormType,
  isTgaRelevant,
} from '@pages/inspection-detail-page-v2/inspection-util';
import { ListViewGroup } from '@shared/components/grouped-list-view/grouped-list-view.component';
import { BuildingSectionResolverService } from '@services/building-section-resolver.service';
import { AddressItemDto, BuildingEquipmentItemDto, BuildingEquipmentService } from '@artemis-software/wr-api';
import { NbTabComponent } from '@nebular/theme';
import {
  BuildingEquipmentTypeGroup,
  groupBuildingEquipmentsByType,
  naturalCollator,
} from '@/utils/building-equipment-order';

@Component({
  standalone: false,
  selector: 'wr-building-equipments-cockpit',
  templateUrl: './building-equipments-cockpit.component.html',
  styleUrls: ['./building-equipments-cockpit.component.scss'],
})
export class BuildingEquipmentsCockpitComponent implements OnInit, OnDestroy {

  readonly getCheckpointString = getCheckpointString;
  readonly getBuildingEquipmentCount = (entry: InspectionEntryFormType): number => {
    return entry.controls.buildingEquipments.value?.length ?? 0;
  };

  readonly formWrapper = input.required<InspectionFormWrapper>();

  private readonly buildingEquipmentService = inject(BuildingEquipmentService);

  relevantInspectionEntries$ = new BehaviorSubject<InspectionEntryFormType[]>([]);
  relevantNonTgaInspectionEntries$ = new BehaviorSubject<InspectionEntryFormType[]>([]);
  unassignedBuildingEquipments$ = new BehaviorSubject<BuildingEquipmentItemDto[]>([]);
  textFilter$ = new BehaviorSubject<string>('');
  unassignedTextFilter$ = new BehaviorSubject<string>('');
  filteredInspectionEntries$: Observable<InspectionEntryFormType[]>;
  filteredNonTgaInspectionEntries$: Observable<InspectionEntryFormType[]>;
  unassignedGroupsView$: Observable<{ groups: BuildingEquipmentTypeGroup[], filterActive: boolean }>;
  filteredInspectionEntriesGrouped$: Observable<ListViewGroup<InspectionEntryFormType>[]>;
  nonTgaRelevantInspectionEntriesGrouped$: Observable<ListViewGroup<InspectionEntryFormType>[]>;
  selectedItem$ = new BehaviorSubject<InspectionEntryFormType | null>(null);

  private readonly destroy$ = new Subject<void>();

  constructor() {
    const buildingSectionResolverService = inject(BuildingSectionResolverService);

    this.filteredInspectionEntries$ = combineLatest([
      this.relevantInspectionEntries$,
      this.textFilter$,
    ]).pipe(
      debounceTime(100),
      map(([relevantEntries, textFilter]) => {
        return relevantEntries.filter((entry) => {
          const entryString = getCheckpointString(entry);
          return entryString.toLowerCase().includes(textFilter.toLowerCase());
        });
      }),
    );

    this.filteredNonTgaInspectionEntries$ = combineLatest([
      this.relevantNonTgaInspectionEntries$,
      this.textFilter$,
    ]).pipe(
      debounceTime(100),
      map(([relevantEntries, textFilter]) => {
        return relevantEntries.filter((entry) => {
          const entryString = getCheckpointString(entry);
          return entryString.toLowerCase().includes(textFilter.toLowerCase());
        });
      }),
    );

    this.unassignedGroupsView$ = combineLatest([
      this.unassignedBuildingEquipments$,
      this.unassignedTextFilter$,
    ]).pipe(
      debounceTime(100),
      map(([items, textFilter]) => {
        const lower = textFilter.toLowerCase();
        const filtered = lower
          ? items.filter(item =>
            item.equipment.name.toLowerCase().includes(lower) ||
            item.sectionText.toLowerCase().includes(lower),
          )
          : items;
        return { groups: groupBuildingEquipmentsByType(filtered), filterActive: !!lower };
      }),
    );

    this.filteredInspectionEntriesGrouped$ = this.filteredInspectionEntries$.pipe(
      switchMap(async (filteredEntries) => {
        const map = new Map<string, { group: ListViewGroup<InspectionEntryFormType>, address: AddressItemDto }>();

        for (const entry of filteredEntries) {
          const sectionId = entry.controls.buildingSectionId.value!;
          const section = await firstValueFrom(buildingSectionResolverService.getBuildingSection(sectionId));
          const address = section.address;
          if (!map.has(sectionId)) {
            map.set(sectionId, {
              group: {
                id: sectionId,
                displayName: `${address.street} ${address.number}`,
                items: [],
              },
              address,
            });
          }
          map.get(sectionId)!.group.items.push(entry);
        }
        this.sortGroupsByCheckpoint(map.values());

        return Array.from(map.values());
      }),
      map((entries) => entries
        .sort((a, b) =>
          naturalCollator.compare(a.address.street ?? '', b.address.street ?? '') ||
          naturalCollator.compare(a.address.number ?? '', b.address.number ?? ''),
        )
        .map(entry => entry.group),
      ),
    );

    this.nonTgaRelevantInspectionEntriesGrouped$ = this.filteredNonTgaInspectionEntries$.pipe(
      switchMap(async (filteredEntries) => {
        const map = new Map<string, { group: ListViewGroup<InspectionEntryFormType>, address: AddressItemDto }>();

        for (const entry of filteredEntries) {
          const sectionId = entry.controls.buildingSectionId.value!;
          const section = await firstValueFrom(buildingSectionResolverService.getBuildingSection(sectionId));
          const address = section.address;
          if (!map.has(sectionId)) {
            map.set(sectionId, {
              group: {
                id: sectionId,
                displayName: `${address.street} ${address.number}`,
                items: [],
              },
              address,
            });
          }
          map.get(sectionId)!.group.items.push(entry);
        }
        this.sortGroupsByCheckpoint(map.values());

        return Array.from(map.values());
      }),
      map((entries) => entries
        .sort((a, b) =>
          naturalCollator.compare(a.address.street ?? '', b.address.street ?? '') ||
          naturalCollator.compare(a.address.number ?? '', b.address.number ?? ''),
        )
        .map(entry => entry.group),
      ),
    );
  }

  ngOnInit(): void {
    this.formWrapper().form$.pipe(
      takeUntil(this.destroy$),
      debounceTime(100),
      filter((form) => !!form),
      map((form) => form!),
    ).subscribe((form) => {
      form.valueChanges.pipe(
        takeUntil(this.destroy$),
        debounceTime(100),
        startWith(form.value),
      ).subscribe(() => {
        this.filterRelevantInspectionEntries(form);
        this.filterNonTgaRelevantInspectionEntries(form);
      });
    });

    combineLatest([this.filteredInspectionEntries$, this.filteredNonTgaInspectionEntries$])
      .pipe(takeUntil(this.destroy$), debounceTime(150))
      .subscribe(([filteredRelevant, filteredNonTga]) => {
        const currentlySelected = this.selectedItem$.value;
        if (currentlySelected) {
          const stillExists =
            filteredRelevant.some(entry => entry.controls.id.value === currentlySelected.controls.id.value) ||
            filteredNonTga.some(entry => entry.controls.id.value === currentlySelected.controls.id.value);

          this.selectedItem$.next(stillExists ? currentlySelected : null);
        }
      });

    combineLatest([
      this.formWrapper().form$.pipe(
        filter((form) => !!form),
        map((form) => form!.controls.id.value as string | undefined),
        filter((id): id is string => !!id),
        distinctUntilChanged(),
      ),
      this.relevantInspectionEntries$,
    ]).pipe(
      takeUntil(this.destroy$),
      debounceTime(300),
      map(([inspectionId]) => inspectionId),
      switchMap((inspectionId) =>
        this.buildingEquipmentService.findNotAssignedForInspection(inspectionId).pipe(
          catchError(() => of([])),
        ),
      ),
    ).subscribe((items) => {
      this.unassignedBuildingEquipments$.next(items);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private filterRelevantInspectionEntries(form: InspectionFormType): void {
    const allEntries = form.controls.inspectionEntries.controls;
    const relevantEntries = allEntries.filter((entry) => isTgaRelevant(entry));
    this.relevantInspectionEntries$.next(relevantEntries);
  }

  private filterNonTgaRelevantInspectionEntries(form: InspectionFormType): void {
    const allEntries = form.controls.inspectionEntries.controls;
    const relevantEntries = allEntries.filter((entry) => {
      return !isTgaRelevant(entry)
        && (entry.controls.buildingEquipments.value?.length ?? 0) > 0;
    });
    this.relevantNonTgaInspectionEntries$.next(relevantEntries);
  }

  private sortGroupsByCheckpoint(
    groups: Iterable<{ group: ListViewGroup<InspectionEntryFormType> }>
  ): void {
    for (const { group } of groups) {
      group.items.sort((a, b) =>
        naturalCollator.compare(
          getCheckpointString(a),
          getCheckpointString(b),
        )
      );
    }
  }

  onTabChange(_: NbTabComponent): void {
    this.selectedItem$.next(null);
  }
}
